import { Fragment } from "react"

import { Ban, Eye, Lock, ShieldCheck } from "lucide-react"

const lenderMatches = [
  { nameWidth: "108px", product: "Term loan · 12–36 months", match: "91%" },
  {
    nameWidth: "146px",
    product: "Line of credit · draw as needed",
    match: "88%",
  },
]

const fundingStats = [
  { value: "$4B+", label: "capital requested" },
  { value: "4k+", label: "businesses supported" },
  { value: "180+", label: "vetted lenders" },
]

const trustPoints = [
  { icon: Lock, label: "No credit pull to start" },
  { icon: Eye, label: "Read-only bank access" },
  { icon: ShieldCheck, label: "Bank-grade encryption" },
  { icon: Ban, label: "Never sold or spammed" },
]

/**
 * The lender-matching card is illustrative, not live data: the borrower has no
 * profile yet at this point in the flow, which is why it is labelled "Preview"
 * and the lender names stay redacted.
 *
 * The accent highlights come from `--brand-accent`, which the theme wrappers set
 * per partner. The match badge pairs the derived soft and strong variants: the
 * rows behind it are a cool grey, so an alpha tint would take on their blue cast
 * and read as grey rather than as the accent.
 */
function LenderMatchesCard() {
  return (
    <div className="w-full rounded-[18px] bg-white px-[26px] py-5 shadow-[0_26px_50px_-22px_rgba(9,20,60,0.55)]">
      <div className="mb-3.5 flex items-center justify-between">
        <div className="text-[15.5px] font-semibold whitespace-nowrap text-[#16265C]">
          Lenders matching your profile
        </div>
        <div className="text-brand-accent flex items-center gap-[7px] text-[12.5px] font-semibold">
          <span className="animate-panel-pulse bg-brand-accent size-[7px] rounded-full motion-reduce:animate-none" />
          Preview
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {lenderMatches.map(({ nameWidth, product, match }) => (
          <div
            key={product}
            className="flex items-center gap-3 rounded-xl bg-[#F5F7FB] px-3 py-[9px]"
          >
            <div
              aria-hidden="true"
              className="flex size-[34px] shrink-0 items-center justify-center rounded-[9px] bg-[#DDE3F1] text-[13px] font-bold text-[#5A6A9C]"
            >
              ◼
            </div>
            <div className="flex flex-1 flex-col gap-[3px]">
              <div
                className="h-[9px] rounded-[5px] bg-[#C9D1E6]"
                style={{ width: nameWidth }}
              />
              <div className="text-xs text-[#8A93A8]">{product}</div>
            </div>
            <div className="bg-brand-accent-soft text-brand-accent-strong shrink-0 rounded-[13px] px-2.5 py-1 text-[12.5px] font-semibold whitespace-nowrap">
              {match} match
            </div>
          </div>
        ))}
      </div>

      <div className="mt-[13px] flex items-center gap-[9px] border-t border-[#EDF0F6] pt-3 text-[13px] text-[#5B6478]">
        <Lock className="text-primary size-4 shrink-0" strokeWidth={1.6} />
        Names, rates and terms unlock as soon as your account exists.
      </div>
    </div>
  )
}

/**
 * The proof panel shown beside the create-account and sign-in forms. It stays
 * identical across every step so the proof holds still while the form advances.
 */
export function QualificationPanel() {
  return (
    <div className="bg-primary relative h-full overflow-hidden">
      <div className="animate-panel-drift pointer-events-none absolute -top-45 -right-40 size-190 rounded-full bg-[radial-gradient(circle_at_35%_35%,rgba(255,255,255,0.13),rgba(255,255,255,0)_65%)] motion-reduce:animate-none" />
      <div className="pointer-events-none absolute -bottom-65 -left-35 size-155 rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.08),rgba(255,255,255,0)_68%)]" />

      <div className="relative mx-auto flex h-full w-full max-w-[868px] flex-col justify-center gap-4 px-16 pt-13 pb-12">
        <div className="flex flex-col gap-4">
          <h2 className="max-w-[720px] text-[40px] leading-[1.1] font-bold tracking-[-0.032em] text-pretty text-white">
            See what you qualify for before you commit to anything.
          </h2>
          <p className="max-w-[660px] text-[17px] leading-[1.5] text-pretty text-white/80">
            One application.{" "}
            <strong className="font-semibold text-white">
              180+ vetted lenders
            </strong>{" "}
            review it in real time and come back with offers.
          </p>
        </div>

        <LenderMatchesCard />

        <div className="flex items-end gap-13">
          {fundingStats.map(({ value, label }, index) => (
            <Fragment key={label}>
              {index > 0 && <div className="h-9.5 w-px bg-white/20" />}
              <div className="flex flex-col gap-0.5">
                <div className="text-[26px] font-bold tracking-[-0.02em] text-white">
                  {value}
                </div>
                <div className="text-[13.5px] text-white/70">{label}</div>
              </div>
            </Fragment>
          ))}
        </div>

        <div className="grid w-full grid-cols-2 gap-x-12 gap-y-3.5 border-t border-white/15 pt-4.5">
          {trustPoints.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-start gap-[11px]">
              <Icon
                className="mt-0.5 size-[18px] shrink-0 text-white"
                strokeWidth={1.6}
              />
              <div className="text-[14.5px] font-semibold text-white">
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
